import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { api } from '@/api/client';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingState, Spinner } from '@/components/Spinner';
import type { SessionStatus } from '@/types/session';
import { getJobStatus } from '@/api/pipeline';
import type { PipelineJobResponse, PipelineStatus } from '@/types/pipeline';

const STEPS: { status: SessionStatus; label: string }[] = [
  { status: 'transcribing', label: '文字起こし' },
  { status: 'section_config_pending', label: 'セクション設定' },
  { status: 'ai_evaluating', label: 'AI 評価' },
  { status: 'feedback_pending', label: 'フィードバック待ち' },
];
const STEP_ORDER: Record<string, number> = {
  transcribing: 0, transcribing_failed: 0, section_config_pending: 1,
  ai_evaluating: 2, ai_evaluation_failed: 2,
  feedback_pending: 3, feedback_in_progress: 3, coach_ack_pending: 4, completed: 4,
};

const PIPELINE_STEPS: { statuses: PipelineStatus[]; label: string }[] = [
  { statuses: ['pending', 'transcribing'], label: '文字起こし' },
  { statuses: ['correcting'], label: 'Gemini 補正' },
  { statuses: ['section_pending'], label: 'セクション設定' },
  { statuses: ['evaluating'], label: 'AI 評価' },
  { statuses: ['completed'], label: '完了' },
];

const PIPELINE_FAIL_STATUSES: PipelineStatus[] = ['failed'];

function pipelineStepIndex(status: PipelineStatus): number {
  for (let i = 0; i < PIPELINE_STEPS.length; i++) {
    if (PIPELINE_STEPS[i]?.statuses.includes(status)) return i;
  }
  return 0;
}

export function ProcessingStatusPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const jobIdQuery = searchParams.get('jobId');
  const isPipelineMode = Boolean(jobIdQuery);

  const [session, setSession] = useState<Record<string, unknown> | null>(null);
  const [pipelineJob, setPipelineJob] = useState<PipelineJobResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchOnce() {
      try {
        if (isPipelineMode && jobIdQuery) {
          const job = await getJobStatus(jobIdQuery);
          if (cancelled) return;
          setPipelineJob(job);
          setError(null);
          // ポーリング: 完了/失敗以外は 5 秒間隔
          const isTerminal =
            job.status === 'completed' || PIPELINE_FAIL_STATUSES.includes(job.status);
          if (!isTerminal) {
            pollTimerRef.current = window.setTimeout(fetchOnce, 5000);
          }
        } else if (sessionId) {
          const r = await api.get(`/sessions/${sessionId}`);
          if (cancelled) return;
          setSession(r.data);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        const msg =
          // @ts-expect-error - userMessage attached by axios interceptor
          err?.userMessage || (err instanceof Error ? err.message : 'データ取得に失敗しました');
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    setLoading(true);
    fetchOnce();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [sessionId, jobIdQuery, isPipelineMode]);

  if (loading) return <div className="p-8 flex justify-center"><LoadingState /></div>;

  if (error) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-800">
          <p className="font-semibold">処理状況の取得に失敗しました</p>
          <p className="mt-2">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
          >
            再読み込み
          </button>
        </div>
      </div>
    );
  }

  if (isPipelineMode && pipelineJob) {
    return <PipelineStatusView job={pipelineJob} sessionId={sessionId} />;
  }

  if (!session) return <div className="p-8 text-center text-red-500">セッションが見つかりません</div>;

  const status = session.status as SessionStatus;
  const currentStep = STEP_ORDER[status] ?? 0;
  const failed = status === 'transcribing_failed' || status === 'ai_evaluation_failed';

  return (
    <div>
      <div className="mb-4"><Link to="/sessions" className="text-sm text-blue-600 hover:underline">← セッション一覧</Link></div>
      <h1 className="text-xl font-semibold text-gray-900">処理状況</h1>
      <p className="mt-1 text-sm text-gray-500">
        {session.client_name as string} / {session.coach_name as string} — {session.day_type as string} #{session.serial_no as number}
      </p>
      <div className="mt-6 rounded-lg bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          {STEPS.map((step, i) => (
            <div key={step.status} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${
                  i < currentStep ? 'bg-green-500 text-white'
                    : i === currentStep && failed ? 'bg-red-500 text-white'
                    : i === currentStep ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-500'}`}>
                  {i < currentStep ? '✓' : i === currentStep && failed ? '!' : i + 1}
                </div>
                <p className={`mt-2 text-xs ${i <= currentStep ? 'font-medium text-gray-900' : 'text-gray-400'}`}>{step.label}</p>
              </div>
              {i < STEPS.length - 1 && <div className={`mx-2 h-0.5 flex-1 ${i < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">現在のステータス:</span>
            <StatusBadge status={status} />
          </div>
          {failed && <button type="button" className="rounded bg-orange-600 px-4 py-2 text-sm text-white hover:bg-orange-700">再試行</button>}
        </div>
      </div>
    </div>
  );
}

function PipelineStatusView({ job, sessionId }: { job: PipelineJobResponse; sessionId?: string }) {
  const status = job.status;
  const failed = PIPELINE_FAIL_STATUSES.includes(status);
  const isCompleted = status === 'completed';
  const isSectionPending = status === 'section_pending';
  const currentStep = pipelineStepIndex(status);
  const sectionConfigUrl = sessionId
    ? `/sessions/${sessionId}/sections?jobId=${encodeURIComponent(job.job_id)}${job.day ? `&day=${encodeURIComponent(job.day)}` : ''}`
    : null;

  return (
    <div>
      <div className="mb-4"><Link to="/sessions" className="text-sm text-blue-600 hover:underline">← セッション一覧</Link></div>
      <h1 className="text-xl font-semibold text-gray-900">処理状況</h1>
      <p className="mt-1 text-sm text-gray-500">
        Job ID: <span className="font-mono">{job.job_id}</span>
        {job.day && ` — ${job.day}`}
        {typeof job.cycle === 'number' && ` (cycle ${job.cycle})`}
      </p>

      <div className="mt-6 rounded-lg bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          {PIPELINE_STEPS.map((step, i) => (
            <div key={step.label} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${
                  i < currentStep ? 'bg-green-500 text-white'
                    : i === currentStep && failed ? 'bg-red-500 text-white'
                    : i === currentStep && !isCompleted ? 'bg-blue-500 text-white'
                    : i === currentStep && isCompleted ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'}`}>
                  {i < currentStep || (i === currentStep && isCompleted) ? '✓' : i === currentStep && failed ? '!' : i + 1}
                </div>
                <p className={`mt-2 text-xs ${i <= currentStep ? 'font-medium text-gray-900' : 'text-gray-400'}`}>{step.label}</p>
              </div>
              {i < PIPELINE_STEPS.length - 1 && (
                <div className={`mx-2 h-0.5 flex-1 ${i < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">現在のステータス:</span>
            <span className="font-mono text-sm text-gray-800">{status}</span>
            {!isCompleted && !failed && <Spinner size="sm" />}
          </div>
        </div>

        {job.progress?.detail && (
          <p className="mt-3 text-xs text-gray-500">{job.progress.detail}</p>
        )}

        {isSectionPending && sectionConfigUrl && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            <p className="font-semibold">セクション設定の入力が必要です</p>
            <p className="mt-1">文字起こしと Gemini 補正が完了しました。続けるにはセクション境界を設定してください。</p>
            <Link
              to={sectionConfigUrl}
              className="mt-3 inline-block rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              セクション設定へ進む →
            </Link>
          </div>
        )}

        {failed && job.error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <p className="font-semibold">エラーが発生しました（{job.error.stage}）</p>
            <p className="mt-1">{job.error.message}</p>
            {job.error.retriable && (
              <p className="mt-2 text-xs text-red-600">※ このエラーはリトライ可能です。</p>
            )}
          </div>
        )}

        {isCompleted && (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            <p className="font-semibold">処理が完了しました</p>
            {job.result?.evaluation_summary && (
              <p className="mt-1">
                評価結果: {job.result.evaluation_summary.evaluation_result}
                {typeof job.result.evaluation_summary.issue_count === 'number' &&
                  ` ／ 指摘 ${job.result.evaluation_summary.issue_count} 件`}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
